import React, { useState } from "react";
import {
  FileText,
  ExternalLink,
  Trash2,
  Sparkles,
  Building,
  ChevronDown,
  ChevronUp,
  Flag,
} from "lucide-react";
import MortgageBreakdownCard from "./MortgageBreakdownCard";

export default function BulkReceiptCard({
  receipt,
  index,
  properties = [],
  vendors = [],
  categories = [],
  onChange,
  onRemove,
}) {
  const [showDetails, setShowDetails] = useState(false);

  const isPdf =
    receipt.file?.type === "application/pdf" ||
    (receipt.receipt_path && receipt.receipt_path.toLowerCase().endsWith(".pdf")) ||
    (receipt.filename && receipt.filename.toLowerCase().endsWith(".pdf"));

  const isMortgage =
    receipt.category === "Mortgage Interest" ||
    (receipt.principal_amount != null && Number(receipt.principal_amount) > 0);

  const updateField = (field, value) => {
    const updated = { ...receipt, [field]: value };

    // Auto update deductible amount if amount or deductible_pct changed
    if (field === "amount" || field === "deductible_pct") {
      const amt = parseFloat(field === "amount" ? value : updated.amount) || 0;
      const pct = parseFloat(field === "deductible_pct" ? value : updated.deductible_pct) ?? 100;
      updated.deductible_amount = Number((amt * (pct / 100)).toFixed(2));
    }

    // If category changed to Mortgage Interest and mortgage fields empty, default them
    if (field === "category" && value === "Mortgage Interest" && !updated.interest_amount) {
      const amt = parseFloat(updated.amount) || 0;
      updated.interest_amount = amt;
      updated.deductible_pct = 100;
      updated.deductible_amount = amt;
    }

    onChange(updated);
  };

  const handleMortgageChange = (mortgageData) => {
    onChange({
      ...receipt,
      amount: mortgageData.amount ? mortgageData.amount.toString() : receipt.amount,
      principal_amount: mortgageData.principalAmount,
      interest_amount: mortgageData.interestAmount,
      escrow_amount: mortgageData.escrowAmount,
      deductible_amount: mortgageData.deductibleAmount,
      deductible_pct: mortgageData.deductiblePct,
      deductibility_reason:
        mortgageData.principalAmount > 0
          ? `Mortgage Principal of $${mortgageData.principalAmount.toFixed(2)} is non-deductible equity reduction; $${mortgageData.deductibleAmount.toFixed(2)} (Interest + Escrow) is Schedule E deductible.`
          : receipt.deductibility_reason,
    });
  };

  const displayName =
    receipt.file?.name ||
    receipt.filename ||
    (receipt.receipt_path ? receipt.receipt_path.split("/").pop() : `Receipt #${index + 1}`);

  const previewUrl =
    receipt.previewUrl ||
    (receipt.receipt_path ? `/api/receipts/${receipt.receipt_path.replace(/^data\//, "")}` : null);

  return (
    <div
      className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 sm:p-5 space-y-4 shadow-sm transition-all hover:border-[var(--border-hover)] min-w-0"
      data-testid={`bulk-receipt-card-${index}`}
    >
      {/* Header: Receipt Index, Thumbnail/Preview, Filename, Status, Remove */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[var(--border)] min-w-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Thumbnail / PDF Badge */}
          {isPdf ? (
            <div className="w-12 h-12 rounded-xl bg-red-500/15 text-red-400 flex flex-col items-center justify-center border border-red-500/25 shrink-0 shadow-xs">
              <FileText size={20} />
              <span className="text-[9px] font-extrabold uppercase mt-0.5">PDF</span>
            </div>
          ) : previewUrl ? (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-xl overflow-hidden border border-[var(--border)] bg-black/20 shrink-0 block relative group"
              title="Click to view receipt"
            >
              <img
                src={previewUrl}
                alt="Receipt thumbnail"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white">
                <ExternalLink size={14} />
              </div>
            </a>
          ) : (
            <div className="w-12 h-12 rounded-xl bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-center text-[var(--muted)] shrink-0">
              <FileText size={20} />
            </div>
          )}

          {/* Name & Badge */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-xs font-black uppercase tracking-wider text-teal-600 dark:text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-md shrink-0">
                Receipt #{index + 1}
              </span>
              <span className="text-sm font-bold text-[var(--text)] truncate max-w-[220px] sm:max-w-xs" title={displayName}>
                {displayName}
              </span>
              {receipt.aiAnalyzed ? (
                <span className="tag bg-teal-500/15 text-teal-500 dark:text-teal-400 border border-teal-500/30 text-[10px] font-bold flex items-center gap-1 shrink-0">
                  <Sparkles size={11} /> Auto-Categorized
                </span>
              ) : (
                <span className="tag bg-slate-500/15 text-slate-600 dark:text-slate-400 text-[10px] font-medium shrink-0">
                  Manual Entry
                </span>
              )}
            </div>

            {/* Subtitle / File Action */}
            <div className="flex items-center gap-3 text-xs text-[var(--muted)] mt-1">
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-500 hover:text-teal-400 font-medium flex items-center gap-1 hover:underline"
                >
                  <ExternalLink size={12} /> View Document
                </a>
              )}
              {receipt.file?.size && (
                <span>{(receipt.file.size / 1024).toFixed(0)} KB</span>
              )}
            </div>
          </div>
        </div>

        {/* Delete Receipt from Batch */}
        <button
          type="button"
          onClick={onRemove}
          className="p-2 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors self-end sm:self-center shrink-0"
          title="Remove this receipt from batch"
          data-testid={`remove-bulk-receipt-${index}`}
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Main Categorization Grid: Category, Amount, Date */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        {/* Category */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
            Category *
          </label>
          <div className="relative">
            <select
              value={receipt.category || "Repairs"}
              onChange={(e) => updateField("category", e.target.value)}
              className="lapen-input text-xs font-semibold py-2"
              data-testid={`bulk-category-${index}`}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
            Amount ($) *
          </label>
          <div className="relative flex items-center">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs pointer-events-none select-none z-10">$</span>
            <input
              type="number"
              step="any"
              value={receipt.amount || ""}
              onChange={(e) => updateField("amount", e.target.value)}
              placeholder="0.00"
              className="lapen-input text-xs font-bold !pl-8 py-2"
              style={{ paddingLeft: "2rem" }}
              data-testid={`bulk-amount-${index}`}
              required
            />
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
            Date *
          </label>
          <input
            type="date"
            value={receipt.date || new Date().toISOString().slice(0, 10)}
            onChange={(e) => updateField("date", e.target.value)}
            className="lapen-input text-xs font-medium py-2"
            data-testid={`bulk-date-${index}`}
            required
          />
        </div>
      </div>

      {/* Property & Vendor Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        {/* Property */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
            Property
          </label>
          <select
            value={receipt.property_id || ""}
            onChange={(e) => updateField("property_id", e.target.value)}
            className="lapen-input text-xs py-2"
            data-testid={`bulk-property-${index}`}
          >
            <option value="">— None (Portfolio-wide) —</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.address ? `(${p.address})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Vendor */}
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
            Vendor / Merchant
          </label>
          <select
            value={receipt.vendor_id || ""}
            onChange={(e) => updateField("vendor_id", e.target.value)}
            className="lapen-input text-xs py-2"
            data-testid={`bulk-vendor-${index}`}
          >
            <option value="">— None / Select Vendor —</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Notes / Description */}
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 block mb-1">
          Notes / Memo
        </label>
        <input
          type="text"
          value={receipt.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder="e.g. Receipt description or memo"
          className="lapen-input text-xs py-2"
          data-testid={`bulk-notes-${index}`}
        />
      </div>

      {/* Mortgage Statement Breakdown (if Mortgage Interest or principal > 0) */}
      {isMortgage && (
        <div className="pt-2">
          <MortgageBreakdownCard
            amount={receipt.amount}
            principalAmount={receipt.principal_amount}
            interestAmount={receipt.interest_amount}
            escrowAmount={receipt.escrow_amount}
            deductibleAmount={receipt.deductible_amount}
            deductiblePct={receipt.deductible_pct}
            onChange={handleMortgageChange}
            readOnly={false}
          />
        </div>
      )}

      {/* Collapsible Tax & CPA Advanced Options */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="text-[11px] text-[var(--muted)] hover:text-[var(--text)] font-semibold flex items-center gap-1 transition-colors"
        >
          {showDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          <span>{showDetails ? "Hide Tax Options" : "More Tax & CPA Options"}</span>
        </button>

        {showDetails && (
          <div className="mt-2.5 p-3 rounded-xl bg-[var(--surface-hover)]/70 border border-[var(--border)] space-y-2.5 text-xs animate-in fade-in duration-150">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-[var(--muted)] mb-1">
                  Deductible %
                </label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    value={receipt.deductible_pct ?? 100}
                    onChange={(e) => updateField("deductible_pct", parseFloat(e.target.value) || 0)}
                    className="lapen-input text-xs py-1.5 pr-7"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs pointer-events-none select-none">%</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[var(--muted)] mb-1">
                  Deductible Amount ($)
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs pointer-events-none select-none z-10">$</span>
                  <input
                    type="number"
                    step="any"
                    value={receipt.deductible_amount ?? ""}
                    onChange={(e) => updateField("deductible_amount", parseFloat(e.target.value) || 0)}
                    className="lapen-input text-xs py-1.5 !pl-8"
                    style={{ paddingLeft: "2rem" }}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1 border-t border-[var(--border)]">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer text-[var(--text)]">
                <input
                  type="checkbox"
                  checked={Boolean(receipt.is_capital_improvement)}
                  onChange={(e) => updateField("is_capital_improvement", e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <Building size={13} className="text-indigo-400" />
                <span>Capital Improvement (Form 4562 Depreciation)</span>
              </label>

              <label className="flex items-center gap-1.5 text-xs cursor-pointer text-[var(--text)]">
                <input
                  type="checkbox"
                  checked={Boolean(receipt.needs_cpa_review)}
                  onChange={(e) => updateField("needs_cpa_review", e.target.checked)}
                  className="rounded text-amber-500 focus:ring-amber-500"
                />
                <Flag size={13} className="text-amber-400" />
                <span>Flag for CPA Review</span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
